/**
 * 編集したおすすめポイントの文 (IN-M-012・DP-M-005・DP-M-019) の読み方と、入力の検査
 * (画面レイアウト_SCR-M-001.md §4.2・§5.1 No.8・§6.4.7、Issue #777)。
 * 「表示する文」の決め方の正本は PBI-002 §11.11「表示する文の決め方」(推薦 PDF と同じ決め方)。
 */

export const REASONS_MAX_LENGTH = 500;

// §5.1 No.8 の文言
export const REASONS_EMPTY_ERROR_MESSAGE = 'おすすめポイントを入力してください。';
export function reasonsTooLongErrorMessage(length) {
    return `おすすめポイントは ${REASONS_MAX_LENGTH} 文字以内で入力してください。（今 ${length} 文字）`;
}

function isNonBlankString(value) {
    return typeof value === 'string' && value.trim() !== '';
}

/**
 * 推薦の要素から、編集した文を読む。空白でない文字列のときだけ「編集した文がある」とし、
 * キーが無い・null・空白だけ・文字列でない値は「編集していない」(null) にする (§6.4.7「編集した文の読み方」)。
 *
 * @param {object|null|undefined} recommendation
 * @returns {string|null}
 */
export function readReasonsOverride(recommendation) {
    const value = recommendation?.reasons_override;
    return isNonBlankString(value) ? value : null;
}

/**
 * 画面に出す文。編集した文があればその文、無ければ AI の文 (reasons)。どちらも無ければ null
 * (「生成できませんでした」を出す。AI の文が空白だけ・文字列でない場合も無いと数える。§4.4)。
 *
 * @param {object|null|undefined} recommendation
 * @returns {string|null}
 */
export function getDisplayedReasons(recommendation) {
    const override = readReasonsOverride(recommendation);
    if (override !== null) return override;
    const reasons = recommendation?.reasons;
    return isNonBlankString(reasons) ? reasons : null;
}

/**
 * 前後の空白 (改行を含む) を除いた文の文字数。コードポイントで数える (サーバの len と同じ。
 * 絵文字等も 1 文字。String#length は UTF-16 の単位で数えるため使わない。§5.1 No.8)。
 *
 * @param {string} text
 * @returns {number}
 */
export function countReasonsLength(text) {
    return Array.from(String(text ?? '').trim()).length;
}

/**
 * 入力欄の文を検査し、保存する文 (前後の空白を除いたもの) を返す (§5.1 No.8)。
 *
 * @param {string} text
 * @returns {{ok: true, value: string} | {ok: false, message: string}}
 */
export function validateReasonsInput(text) {
    const trimmed = String(text ?? '').trim();
    if (trimmed === '') return { ok: false, message: REASONS_EMPTY_ERROR_MESSAGE };
    const length = countReasonsLength(trimmed);
    if (length > REASONS_MAX_LENGTH) return { ok: false, message: reasonsTooLongErrorMessage(length) };
    return { ok: true, value: trimmed };
}

/**
 * 推薦の要素に、保存後の「編集した文」を入れた新しい要素を返す (元の要素は変えない)。
 * 編集していない (null) なら、キーごと外す。
 *
 * @param {object} recommendation
 * @param {string|null} override
 * @returns {object}
 */
export function withReasonsOverride(recommendation, override) {
    const { reasons_override: _removed, ...rest } = recommendation;
    return override === null ? rest : { ...rest, reasons_override: override };
}
